import {
  type BoxProps,
  Card,
  Flex,
  HelpIcon,
  HStack,
  InlineFormattedNumber,
  isOrderComplete,
  type Order,
  Text,
} from '@talos/kyoko';
import { useDDHCalculateHedgeQty } from '../../../../../../../components/DDH/useDDHCalculateHedgeQty';
import { useFeatureFlag } from '../../../../../../../hooks';
import { DDHHedgeOrderStatusMissingCard } from './DDHHedgeOrderStatusMissingCard';
import { DDHParentOrderDetailsBlotter } from './DDHParentOrderDetailsBlotter';

interface DDHDetailsProps extends BoxProps {
  order: Order;
}

/**
 * This component shows DDH details for a DDH Parent Order. The passed in order should be a DDH Parent Order.
 */
export const DDHParentOrderDetails = ({ order, ...boxProps }: DDHDetailsProps) => {
  const { enableDynamicDeltaHedging } = useFeatureFlag();

  if (!enableDynamicDeltaHedging || !order.isDDHParentOrder) {
    return null;
  }

  const isComplete = isOrderComplete(order.OrdStatus);

  return (
    <Flex flexDirection="column" w="100%" gap="spacing8" {...boxProps}>
      {!isComplete && <EstimatedHedgeQty order={order} />}
      {!order.HedgeOrderStatus && <DDHHedgeOrderStatusMissingCard />}
      {order.HedgeOrderStatus && (
        <Card h="98px" data-testid="ddh-parent-order-blotter">
          {/* Bit of a magic number height here. Just high enough to render header + 1 row without + 8px for a scrollbar. */}
          <DDHParentOrderDetailsBlotter order={order} />
        </Card>
      )}
    </Flex>
  );
};

interface EstimatedHedgeQtyProps {
  order: Order;
}

function EstimatedHedgeQty({ order }: EstimatedHedgeQtyProps) {
  const hedgeCurrency = order.HedgeOrderStatus?.Currency;

  const { estimatedHedgeQty, delta } = useDDHCalculateHedgeQty(order, true);
  if (!estimatedHedgeQty || estimatedHedgeQty.eq(0)) {
    return null;
  }

  return (
    <HStack justifyContent="space-between" gap="spacing8">
      <Card flexBasis="50%" h="100%">
        <HStack justifyContent="space-between" gap="spacing8">
          <Text>
            Estimated Hedge Qty{' '}
            <HelpIcon tooltip="Current Total Fill on Parent Order (in contracts)*multiplier*Delta of the option per contract. Please note that it could be different from the actual hedge quantity." />
          </Text>
          <InlineFormattedNumber number={estimatedHedgeQty.toFixed()} currency={hedgeCurrency} />
        </HStack>
      </Card>
      <Card flexBasis="50%" h="100%">
        <HStack justifyContent="space-between" gap="spacing8">
          <Text>
            Delta per Options Contract (Live){' '}
            <HelpIcon tooltip="Current Delta per Options Contract, as sent by the Exchange." />
          </Text>
          <InlineFormattedNumber number={delta?.toFixed()} />
        </HStack>
      </Card>
    </HStack>
  );
}
